import React from "react";
import { LineSeries, ScadaDatetimeTagDataSource } from "interfaces/chart";
import {
  IconButton,
  List,
  ListItemButton,
  ListItemSecondaryAction
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { styled } from "@mui/material/styles";
import { MenuTooltip } from "components/DataExplorationV2/MenuTooltip";

export interface CurrentSeriesProps {
  /** The status of API fetch for Parameters(Tags) and Machines */
  loading: boolean;
  error: boolean;
  /** Series related props */
  series: any[] | null;
  selectedSeries: LineSeries<ScadaDatetimeTagDataSource> | null;
  onAddSeries: () => void;
  onChangeSelectedSeries: (
    series: LineSeries<ScadaDatetimeTagDataSource>
  ) => void;
  onClickDeleteSeries: (event: React.UIEvent<{}>, seriesId: string) => void;
}

export const CurrentSeries = ({
  loading,
  error,
  series,
  selectedSeries,
  onChangeSelectedSeries,
  onClickDeleteSeries
}: CurrentSeriesProps): JSX.Element => {
  return (
    <div aria-label="Current Series Wrapper">
      <CurrentSeriesSubtitle>
        {series?.length ? "Current series" : "Add series"}
      </CurrentSeriesSubtitle>
      {series && series?.length > 0 ? (
        <StyledList>
          {series.map(
            (el: LineSeries<ScadaDatetimeTagDataSource>, index: number) => {
              return (
                <StyledListItemButton
                  key={`Series_${index}`}
                  aria-label="current-series-row"
                  disabled={loading || error}
                  selected={el.id === selectedSeries?.id}
                  onClick={() => onChangeSelectedSeries(el)}
                >
                  <MenuTooltip title={el.title}>
                    <SeriesTitle>{el.title}</SeriesTitle>
                  </MenuTooltip>
                  <ListItemSecondaryAction>
                    <MenuTooltip title="Delete">
                      <SecondaryButton
                        aria-label="delete-series-button"
                        onClick={(
                          event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                        ) => onClickDeleteSeries(event, el.id)}
                      >
                        <DeleteIcon fontSize="small" color="secondary" />
                      </SecondaryButton>
                    </MenuTooltip>
                  </ListItemSecondaryAction>
                </StyledListItemButton>
              );
            }
          )}
        </StyledList>
      ) : null}
    </div>
  );
};

const CurrentSeriesSubtitle = styled("div")`
  display: flex;
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  font-size: 0.75rem;
  align-items: center;
  justify-content: space-between;
`;

const SecondaryButton = styled(IconButton)`
  width: 20px;
  height: 20px;
  padding: 0 !important;
  .MuiSvgIcon-root {
    transition: all 150ms ease-out;
    &:hover {
      color: #19173f !important;
      opacity: 0.8;
    }
  }
`;

const StyledList = styled(List)`
  font-size: 0.75rem;
  margin: 0 -${({ theme }) => theme.spacing(2)} !important;
  padding: 0 ${({ theme }) => theme.spacing(2)};
`;

const StyledListItemButton = styled(ListItemButton)`
  height: 36px;
  &.MuiListItemButton-root:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  &.MuiListItemButton-root.Mui-selected {
    background-color: rgba(0, 0, 0, 0.1);
  }
  &.MuiListItemButton-root.Mui-selected:hover {
    background-color: rgba(0, 0, 0, 0.15);
  }
`;

const SeriesTitle = styled("div")`
  word-break: break-word;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
